/**
 * Routing rules for ludicrous-gateway
 * Defines rule structure and default rules
 */

import type { RoutingRuleConfig, RoutingCondition } from "../types/config.js";
import type { CompiledRoutingRule } from "../types/routing.js";

/**
 * Compile a routing condition into efficient matchers
 */
export function compileCondition(condition: RoutingCondition): {
  keywords: Set<string>;
  patterns: RegExp[];
  models: Set<string>;
  minTokens?: number | undefined;
  maxTokens?: number | undefined;
  hasTools?: boolean | undefined;
} {
  return {
    keywords: new Set(
      (condition.keywords ?? []).map((k) => k.toLowerCase())
    ),
    patterns: (condition.patterns ?? []).map((p) => {
      try {
        return new RegExp(p, "i");
      } catch {
        // Invalid regex, return a never-matching pattern
        return /(?!)/;
      }
    }),
    models: new Set(condition.models ?? []),
    minTokens: condition.min_tokens,
    maxTokens: condition.max_tokens,
    hasTools: condition.has_tools,
  };
}

/**
 * Compile a routing rule for efficient matching
 */
export function compileRule(rule: RoutingRuleConfig): CompiledRoutingRule {
  const compiled = compileCondition(rule.conditions);

  return {
    name: rule.name,
    priority: rule.priority,
    enabled: rule.enabled ?? true,
    destinationProvider: rule.route_to.provider,
    destinationModel: rule.route_to.model,
    keywords: compiled.keywords,
    patterns: compiled.patterns,
    minTokens: compiled.minTokens,
    maxTokens: compiled.maxTokens,
    models: compiled.models,
    hasTools: compiled.hasTools,
  };
}

/**
 * Compile multiple rules and sort by priority
 */
export function compileRules(rules: RoutingRuleConfig[]): CompiledRoutingRule[] {
  return rules
    .filter((r) => r.enabled !== false)
    .map(compileRule)
    .sort((a, b) => b.priority - a.priority); // Higher priority first
}

/**
 * Default routing rules
 * These provide sensible defaults for common scenarios
 */
export const defaultRules: RoutingRuleConfig[] = [
  // Route sensitive content to local provider
  {
    name: "sensitive-content-local",
    conditions: {
      keywords: [
        "API key",
        "password",
        "secret",
        ".env",
        "private_key",
        "credential",
      ],
    },
    route_to: {
      provider: "ollama",
      model: "qwen3:32b",
    },
    priority: 100,
    enabled: true,
  },

  // Route very large requests to specialized provider
  {
    name: "large-context",
    conditions: {
      min_tokens: 50000,
    },
    route_to: {
      provider: "anthropic",
    },
    priority: 80,
    enabled: true,
  },

  // Route quick queries to faster provider
  {
    name: "quick-queries",
    conditions: {
      max_tokens: 1000,
    },
    route_to: {
      provider: "zai",
    },
    priority: 50,
    enabled: true,
  },
];

/**
 * Validate a routing rule configuration
 */
export function validateRule(rule: RoutingRuleConfig): string[] {
  const errors: string[] = [];

  if (!rule.name || rule.name.trim() === "") {
    errors.push("Rule name is required");
  }

  if (!rule.route_to?.provider) {
    errors.push("Rule must have a destination provider");
  }

  const hasConditions =
    (rule.conditions.keywords?.length ?? 0) > 0 ||
    (rule.conditions.patterns?.length ?? 0) > 0 ||
    (rule.conditions.models?.length ?? 0) > 0 ||
    rule.conditions.min_tokens !== undefined ||
    rule.conditions.max_tokens !== undefined ||
    rule.conditions.has_tools !== undefined;

  if (!hasConditions) {
    errors.push("Rule must have at least one condition");
  }

  // Validate regex patterns
  if (rule.conditions.patterns) {
    for (const pattern of rule.conditions.patterns) {
      try {
        new RegExp(pattern);
      } catch {
        errors.push(`Invalid regex pattern: ${pattern}`);
      }
    }
  }

  // Validate token ranges
  if (
    rule.conditions.min_tokens !== undefined &&
    rule.conditions.max_tokens !== undefined &&
    rule.conditions.min_tokens > rule.conditions.max_tokens
  ) {
    errors.push("min_tokens cannot be greater than max_tokens");
  }

  return errors;
}
